if (!customElements.get('bundle-product')) {
  customElements.define('bundle-product', class BundleProduct extends HTMLElement {
    connectedCallback() {
      this.bundleType   = this.dataset.bundleType;
      this.bundleHandle = this.dataset.bundleHandle;
      this.packQuantity = Number(this.dataset.packQuantity) || 0;
      this.variantMap   = JSON.parse(this.querySelector('[data-bundle-variants]')?.textContent || '{}');
      this.atcBtn       = this.querySelector('[data-bundle-atc]');
      this.errorEl      = this.querySelector('[data-bundle-error]');
      this.items        = [...this.querySelectorAll('[data-bundle-item]')];
      this.rows         = [...this.querySelectorAll('[data-multipack-row]')];
      this.multipack    = this.querySelector('[data-multipack]');
      this.globalSelect = this.querySelector('fancy-select[data-global-size]');
      this.slots        = {};
      this.itemsToAdd   = [];
      this.state        = 'incomplete';

      if (!this.atcBtn) return;

      this.initDefaults();
      this.bind();
      this.update();
    }

    // — Setup —

    initDefaults() {
      // Multipack: preselect the first product in each slot (matches legacy behavior)
      this.rows.forEach(row => {
        const first = row.querySelector('[data-row-product]');
        if (first) this.slots[row.dataset.multipackRow] = first.dataset.productHandle;
      });
    }

    bind() {
      this.addEventListener('change', e => {
        const select = e.target.closest('fancy-select');
        if (!select) return;
        this.clearError(select);
        this.update();
      });

      this.addEventListener('click', e => {
        const swatch = e.target.closest('[data-row-product]');
        if (!swatch || swatch.disabled) return;
        const row = swatch.closest('[data-multipack-row]');
        this.slots[row.dataset.multipackRow] = swatch.dataset.productHandle;
        this.update();
      });

      this.atcBtn.addEventListener('click', () => this.handleAtc());
    }

    // — Variant lookup —

    findVariant(handle, size) {
      const entry = this.variantMap[handle];
      if (!entry) return null;
      return entry.variants.find(v =>
        v.available && (size == null || entry.sizeIndex == null || v.options[entry.sizeIndex] === size)
      ) || null;
    }

    getItemSize(item) {
      const select = item.querySelector('fancy-select[data-bundle-size]');
      if (select) return select.value || '';
      return item.dataset.defaultSize ?? null;
    }

    getGlobalSize() {
      if (this.globalSelect) return this.globalSelect.value || '';
      return this.multipack?.dataset.defaultSize ?? null;
    }

    // — State —

    update() {
      let incomplete = false;
      let unavailable = false;
      const items = [];

      if (this.bundleType === 'bundle') {
        this.items.forEach(item => {
          const size = this.getItemSize(item);
          if (size === '') { incomplete = true; return; }

          const variant = this.findVariant(item.dataset.productHandle, size);
          if (!variant) { unavailable = true; return; }

          items.push({ id: variant.id, quantity: Number(item.dataset.quantity) || 1 });
        });
      }

      if (this.bundleType === 'multipack') {
        const size = this.getGlobalSize();
        if (size === '') incomplete = true;

        this.rows.forEach(row => {
          const index   = row.dataset.multipackRow;
          const buttons = [...row.querySelectorAll('[data-row-product]')];

          buttons.forEach(btn => {
            btn.disabled = !!size && !this.findVariant(btn.dataset.productHandle, size);
          });

          // Selected product isn't offered in this size: fall back to the first one that is
          const selectedBtn = buttons.find(b => b.dataset.productHandle === this.slots[index]);
          if (!selectedBtn || selectedBtn.disabled) {
            this.slots[index] = buttons.find(b => !b.disabled)?.dataset.productHandle;
          }

          const handle = this.slots[index];
          buttons.forEach(btn => btn.setAttribute('aria-pressed', String(btn.dataset.productHandle === handle)));

          const titleEl = row.querySelector('[data-row-title]');
          const title   = buttons.find(b => b.dataset.productHandle === handle)?.dataset.productTitle;
          if (titleEl) titleEl.textContent = title ? `: ${title}` : '';

          if (!handle) { unavailable = true; return; }
          if (size === '') return;

          const variant = this.findVariant(handle, size);
          if (!variant) { unavailable = true; return; }

          items.push({ id: variant.id, quantity: 1 });
        });
      }

      if (!this.bundleType || (!this.items.length && !this.rows.length)) unavailable = true;

      this.itemsToAdd = items;
      this.state = unavailable ? 'unavailable' : incomplete ? 'incomplete' : 'ready';
      this.updateAtc();
    }

    updateAtc() {
      const { labelSelect, labelAdd, labelSoldOut } = this.dataset;
      const unavailable = this.state === 'unavailable';

      this.atcBtn.textContent = unavailable ? labelSoldOut : this.state === 'incomplete' ? labelSelect : labelAdd;
      this.atcBtn.disabled = unavailable;
      this.atcBtn.classList.toggle('disabled', this.state !== 'ready');
      this.atcBtn.classList.toggle('cursor-not-allowed!', unavailable);
      if (this.state === 'ready') this.errorEl?.classList.add('hidden');
    }

    // — Errors —

    showSelectErrors() {
      this.querySelectorAll('fancy-select').forEach(fs => {
        if (fs.value) return;
        fs.classList.add('fs-error-state');
        const err = fs.nextElementSibling;
        if (err?.classList.contains('fs-error')) err.classList.remove('hidden');
      });
    }

    clearError(select) {
      select.classList.remove('fs-error-state');
      const err = select.nextElementSibling;
      if (err?.classList.contains('fs-error')) err.classList.add('hidden');
    }

    // — Cart —

    handleAtc() {
      if (this.state === 'incomplete') { this.showSelectErrors(); return; }
      if (this.state !== 'ready') return;
      this.addToCart();
    }

    addToCart() {
      // _bundle_id / _bundle_size are read by the bundler discount function — keep names in sync
      const bundleSize = this.bundleType === 'multipack' && this.packQuantity
        ? this.packQuantity
        : this.itemsToAdd.length;

      const items = this.itemsToAdd.map(i => ({
        id: i.id,
        quantity: i.quantity,
        properties: {
          _bundle_id: this.bundleHandle,
          _bundle_size: bundleSize,
        },
      }));

      window.dispatchEvent(new CustomEvent('modal:openById', { detail: 'minicart' }));
      const loadingWrapper = document.querySelector('[data-cart-loading-wrapper]');
      loadingWrapper?.classList.add('loading');
      this.atcBtn.disabled = true;
      this.errorEl?.classList.add('hidden');

      // Match global.js: keep the spinner visible while the minicart animates in
      const min_ms = 500;
      const start  = Date.now();

      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items,
          ...(window.minicartSectionId && { sections: [window.minicartSectionId] }),
        }),
      })
        .then(async res => {
          const data = await res.json();
          if (!res.ok) throw new Error(data.description || data.message || res.statusText);
          return data;
        })
        .then(data => {
          const delay = Math.max(0, min_ms - (Date.now() - start));
          setTimeout(() => window.dispatchEvent(new CustomEvent('productForm:add', { detail: data })), delay);
        })
        .catch(err => {
          console.error(err);
          loadingWrapper?.classList.remove('loading');
          window.dispatchEvent(new CustomEvent('modal:closeById', { detail: 'minicart' }));
          if (this.errorEl) {
            this.errorEl.textContent = err.message || this.dataset.labelError;
            this.errorEl.classList.remove('hidden');
          }
        })
        .finally(() => {
          this.atcBtn.disabled = this.state === 'unavailable';
        });
    }
  });
}
